<!--
  工作台顶部的「待审核 N（超时 M）」计数 + 筛选 + 「只看我的」开关
  （REQ-2026-574277 二期修订 §4）

  口径（2026-09-18 产品定）：
    1. 计数与列表**底层取全部**待审 —— 管理员进来不会看到空页
    2. 「只看我的」开关：当前登录人能映射到有效客服时**默认打开**（§1.3 按人落地），
       映射不到时**默认关闭并禁用**，提示"当前账号不是客服"
-->
<template>
  <div class="ai-review-bar">
    <span
      class="count"
      :class="{ active: filtering, clickable: shownPending > 0 || filtering, flash: flashing }"
      @click="toggleFilter"
    >
      待审核 <b>{{ shownPending }}</b>
      <template v-if="shownOverdue > 0">（超时 <b class="overdue">{{ shownOverdue }}</b>）</template>
      <i v-if="filtering" class="el-icon-close" />
    </span>
    <el-tooltip :disabled="isStaff" content="当前账号不是客服" placement="top">
      <span class="mine">
        <el-switch v-model="onlyMine" :disabled="!isStaff" active-text="只看我的" @change="onMineChange" />
      </span>
    </el-tooltip>
  </div>
</template>

<script>
import aiReviewApi from '@/api/im/aiReview'
import { primeOnFirstInteraction, beep } from '@/utils/csRemind'

/** cust_service_ai_review.status：1 待审核 */
const STATUS_PENDING = 1
/** 计数刷新间隔。草稿不会在几秒内变多，30 秒足够 */
const REFRESH_MS = 30000
/**
 * 提醒的合并窗口。客户发一条消息会**同时**触发两件事：H5 未读 +1、新草稿 +1，
 * 它们分别从两个接口回来、先后差几百毫秒。不合并的话客服会听到连着两声"嘀"，以为来了两个客户。
 */
const REMIND_MERGE_MS = 1500
/** 角标闪多久 */
const FLASH_MS = 6000

export default {
  name: 'AiReviewBar',
  data() {
    return {
      counts: { pending: 0, overdue: 0, minePending: 0, mineOverdue: 0 },
      isStaff: false,
      onlyMine: false,
      // 「只看我的」默认值只在**第一次**拿到 isStaff 时定一次。
      // 之后每 30 秒刷新计数时不能再覆盖它，否则客服手动关掉开关、半分钟后又自己弹回去。
      mineDefaultApplied: false,
      filtering: false,
      timer: null,
      // ---- §10.3 提醒 ----
      // 受 cust_service_staff.is_new_msg_remind 控制，值由 pendingCount 接口顺带返回
      remindEnabled: false,
      // 上一拍的「我的待审」数。null = 还没有上一拍（首次加载不提醒：那是存量，不是"新来的"）
      lastMinePending: null,
      lastH5Unread: null,
      remindTimer: null,
      flashTimer: null,
      flashing: false
    }
  },
  computed: {
    shownPending() { return this.onlyMine ? this.counts.minePending : this.counts.pending },
    shownOverdue() { return this.onlyMine ? this.counts.mineOverdue : this.counts.overdue }
  },
  created() {
    this.refresh()
    this.$EventBus.$on('aiReviewChanged', this.refresh)
    this.$EventBus.$on('csH5UnreadTotal', this.onH5UnreadTotal)
    // 提示音要等用户第一次点击之后才放得出来（浏览器自动播放限制），先把监听挂上
    primeOnFirstInteraction()
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.startTimer()
  },
  beforeDestroy() {
    this.$EventBus.$off('aiReviewChanged', this.refresh)
    this.$EventBus.$off('csH5UnreadTotal', this.onH5UnreadTotal)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.stopTimer()
    if (this.remindTimer) clearTimeout(this.remindTimer)
    if (this.flashTimer) clearTimeout(this.flashTimer)
  },
  methods: {
    /**
     * 工作台**唯一的** 30 秒定时器。每一拍做两件事：刷草稿计数、通知会话列表刷新。
     *
     * 为什么只能有一个：计数和列表各跑各的 30 秒定时器的话，两者相位不同 ——
     * 客服会看到"消息已经到了、待审数却没变"（或反过来），看起来像其中一个坏了。
     *
     * ⚠️ 页面不可见时定时器**真的停掉**，不是"还在跑、只是跳过"：
     *    客服把工作台标签页挂在后台一整天是常态。切回可见时**立即补一拍**，不等下一个周期。
     */
    startTimer() {
      this.stopTimer()
      if (document.hidden) return
      this.timer = setInterval(this.tick, REFRESH_MS)
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.stopTimer()
      } else {
        this.tick()
        this.startTimer()
      }
    },
    tick() {
      this.refresh()
      // 会话列表（chatList）听这个事件做"安静刷新"：H5 客户发来的消息没有腾讯 IM 事件，只能靠这一拍
      this.$EventBus.$emit('csWorkbenchTick')
    },
    /** 会话列表每次刷新完，把 H5 会话的未读总数报过来 */
    onH5UnreadTotal(total) {
      const n = Number(total) || 0
      if (this.lastH5Unread !== null && n > this.lastH5Unread) this.queueRemind()
      this.lastH5Unread = n
    },
    /**
     * 提醒：角标闪 + 一声"嘀"。REMIND_MERGE_MS 内的多次触发合并成一次。
     *
     * ⚠️ 角标闪**不依赖**声音是否放得出来：刷新页面后一次都没点过的话，
     *    AudioContext 还是 suspended，beep() 会安静地返回 false —— 这时至少角标得闪。
     */
    queueRemind() {
      if (!this.remindEnabled) return
      if (this.remindTimer) clearTimeout(this.remindTimer)
      this.remindTimer = setTimeout(() => {
        this.remindTimer = null
        this.flashing = true
        if (this.flashTimer) clearTimeout(this.flashTimer)
        this.flashTimer = setTimeout(() => { this.flashing = false }, FLASH_MS)
        beep()
      }, REMIND_MERGE_MS)
    },
    refresh() {
      aiReviewApi.pendingCount().then(({ data }) => {
        const d = data || {}
        this.counts = {
          pending: d.pending || 0,
          overdue: d.overdue || 0,
          minePending: d.minePending || 0,
          mineOverdue: d.mineOverdue || 0
        }
        // ⚠️ 两组数是**同一次请求**返回的。不要拆成两个接口各取各的：
        //    两次请求之间数据会变，开关一切换数字就对不上，看起来像 bug。
        this.isStaff = d.isStaff === 1
        this.remindEnabled = this.isStaff && d.newMsgRemind === 1
        // 「我的待审」比上一拍多了 = 来了新草稿。用 mine 不用全局：别人名下来了新草稿不该吵到我
        const mine = d.minePending || 0
        if (this.lastMinePending !== null && mine > this.lastMinePending) this.queueRemind()
        this.lastMinePending = mine
        if (!this.mineDefaultApplied) {
          this.mineDefaultApplied = true
          this.onlyMine = this.isStaff
        }
        if (!this.isStaff) this.onlyMine = false
        // 筛选开着时，待审集合可能变了（有人处理掉了一条），跟着刷新
        if (this.filtering) this.applyFilter()
      }).catch(() => {
        // 计数接口挂了不能连累聊天：保持上一次的数字，不弹错
      })
    },
    onMineChange() {
      if (this.filtering) this.applyFilter()
    },
    toggleFilter() {
      if (this.filtering) {
        this.filtering = false
        this.$EventBus.$emit('aiReviewFilter', null)
        return
      }
      if (this.shownPending <= 0) return
      this.filtering = true
      this.applyFilter()
    },
    /**
     * 把"有待审草稿的客户"发给会话列表去筛。
     *
     * ⚠️ 用 **customerId** 对，不用会话 id：
     *    草稿队列存的是**客户侧**会话行的 id，会话列表里是**客服侧镜像行**的 id，两个数不相等。
     *    会话列表是"我的会话"，同一客户只有一条，所以按客户对是准的。
     */
    applyFilter() {
      aiReviewApi.fetchList({
        status: STATUS_PENDING,
        onlyMine: this.onlyMine,
        pageNo: 1,
        pageSize: 200
      }).then(({ data }) => {
        if (!this.filtering) return
        const list = (data && data.list) || []
        const ids = Array.from(new Set(list.map(r => r.customerId).filter(v => v != null)))
        this.$EventBus.$emit('aiReviewFilter', ids)
      }).catch(() => {
        // 取不到就退出筛选，别让列表停在一个空的筛选态上
        this.filtering = false
        this.$EventBus.$emit('aiReviewFilter', null)
      })
    }
  }
}
</script>

<style scoped>
/* §10.3 新草稿 / H5 新消息提醒：闪几秒自己停 */
.count.flash { animation: ai-review-flash 1s ease-in-out infinite; }
@keyframes ai-review-flash {
  0%, 100% { background: transparent; }
  50% { background: #fde2e2; }
}
.ai-review-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  font-size: 13px;
  color: #606266;
  border-bottom: 1px solid #f0f0f0;
}
.count.clickable { cursor: pointer; }
.count.active { color: #e6a23c; }
.count b { color: #e6a23c; margin: 0 2px; }
.count b.overdue { color: #f56c6c; }
.count i { margin-left: 4px; }
</style>
